// ---------------------------------------------------------------------------
// 편집영역 기하 검사 — 전부 순수 함수. zod 계약(editor.ts)의 superRefine 이 호출하고,
// 어드민 편집 화면이 저장 전 검사에 같은 함수를 재사용한다.
// 좌표계는 editor.ts 의 EDIT_AREA_COORDINATE_SYSTEM 이 정한다:
// 서피스 제어점은 사진 %(원점 좌상단, y 아래), 배치 위치는 서피스 realSize mm 프레임.
// 윤곽(outline)은 서피스 제어점과 같은 사진 % 좌표다.
// ---------------------------------------------------------------------------

import type { ComponentShape, ShapePoint, ShapeVertex } from './editor';

export interface SurfacePoint {
  readonly x: number;
  readonly y: number;
}

export type FlatSurfacePoints = readonly [SurfacePoint, SurfacePoint, SurfacePoint, SurfacePoint];

export interface SizeMm {
  readonly widthMm: number;
  readonly heightMm: number;
}

export interface PlacementPosition {
  readonly xMm: number;
  readonly yMm: number;
}

export interface MeshCellIndex {
  readonly row: number;
  readonly column: number;
}

/** 부동소수 비교 허용오차 — 사진 % 와 mm 모두 이 아래의 차이는 같은 값으로 본다. */
export const GEOMETRY_TOLERANCE = 1e-6;

const isNear = (a: number, b: number): boolean => Math.abs(a - b) <= GEOMETRY_TOLERANCE;

/**
 * a→b→c 의 회전 방향. y 아래 좌표계에서 양수면 화면 기준 시계 방향(TL→TR→BR 순서)이다.
 * 0 이면 세 점이 한 줄에 놓인 것이다.
 */
const turn = (a: SurfacePoint, b: SurfacePoint, c: SurfacePoint): number =>
  (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);

/**
 * 네 점(TL·TR·BR·BL 순서)이 볼록이고 방향이 일관된 사각형인가.
 * 네 모서리의 회전 방향이 전부 양수여야 한다 — 하나라도 0 이하면 뒤집혔거나 오목하거나 넓이가 0 이다.
 */
const isProperQuad = ([tl, tr, br, bl]: FlatSurfacePoints): boolean =>
  turn(tl, tr, br) > 0 && turn(tr, br, bl) > 0 && turn(br, bl, tl) > 0 && turn(bl, tl, tr) > 0;

/**
 * flat 서피스 제어점 4개(TL·TR·BR·BL)가 축 정렬 직사각형이고 넓이가 0 보다 큰가.
 * 정책 §2-3: flat 은 정면 평면의 직사각형이다. 기울어진 평면은 mesh 2×2 로 잡는다.
 */
export const isAxisAlignedRect = (points: FlatSurfacePoints): boolean => {
  const [tl, tr, br, bl] = points;

  return (
    isNear(tl.y, tr.y) &&
    isNear(bl.y, br.y) &&
    isNear(tl.x, bl.x) &&
    isNear(tr.x, br.x) &&
    tr.x - tl.x > GEOMETRY_TOLERANCE &&
    bl.y - tl.y > GEOMETRY_TOLERANCE
  );
};

const pointAt = (points: readonly SurfacePoint[], index: number): SurfacePoint => {
  const point = points[index];
  if (point === undefined) {
    throw new RangeError(`mesh 제어점 ${index} 가 없습니다.`);
  }
  return point;
};

/**
 * mesh 격자에서 뒤집혔거나 오목하거나 넓이가 0 인 첫 셀(row-major)을 찾는다. 없으면 null.
 * points 는 row-major(왼쪽 위부터 오른쪽으로, 다음 행) 이며 길이가 columns×rows 여야 한다 —
 * 길이 검사는 호출자(zod) 책임이고 어긋나면 RangeError 다.
 * sin 투영 열 간격·타원 가장자리처럼 행이 y 방향으로 단조롭지 않은 격자도
 * 셀마다 볼록·방향 일관이면 통과한다.
 */
export const findFlippedMeshCell = (
  columns: number,
  rows: number,
  points: readonly SurfacePoint[],
): MeshCellIndex | null => {
  if (points.length !== columns * rows) {
    throw new RangeError(`mesh 제어점은 columns×rows(${columns * rows})개여야 합니다.`);
  }

  for (let row = 0; row < rows - 1; row += 1) {
    for (let column = 0; column < columns - 1; column += 1) {
      const cell: FlatSurfacePoints = [
        pointAt(points, row * columns + column),
        pointAt(points, row * columns + column + 1),
        pointAt(points, (row + 1) * columns + column + 1),
        pointAt(points, (row + 1) * columns + column),
      ];

      if (!isProperQuad(cell)) {
        return { row, column };
      }
    }
  }

  return null;
};

/**
 * 배치(컴포넌트 실측 mm × 서피스 안 위치)가 서피스 실측 범위 안에 있는가. 경계에 닿는 것은 허용한다.
 * 정책 §1: 배치는 서피스 안에서의 위치만 가지며 크기는 컴포넌트 mm 가 정한다.
 */
export const isPlacementInsideSurface = (
  component: SizeMm,
  surface: SizeMm,
  position: PlacementPosition,
): boolean =>
  position.xMm >= 0 &&
  position.yMm >= 0 &&
  position.xMm + component.widthMm <= surface.widthMm + GEOMETRY_TOLERANCE &&
  position.yMm + component.heightMm <= surface.heightMm + GEOMETRY_TOLERANCE;

// ---------------------------------------------------------------------------
// 원통(cylinder) — 곡률 비율은 위·아래 경계 양 끝의 이동량 ÷ 영역 높이이고, 양 끝이 아래로 가면 + 다.
// 엔진(@repo/warp-engine validateCylinder)과 같은 식을 사진 없이 비율로 판정한다.
// ---------------------------------------------------------------------------

/**
 * 위·아래 곡선이 만나거나 교차하는가. 양 끝 열의 높이는 영역 높이 × (1 + bottom − top) 이라
 * bottom − top 이 −1 이하이면 두 곡선이 닿는다. 엔진의 엄격 부등호보다 허용오차만큼 엄격하다.
 */
export const doCylinderCurvesCross = (topCurveRatio: number, bottomCurveRatio: number): boolean =>
  bottomCurveRatio - topCurveRatio <= -1 + GEOMETRY_TOLERANCE;

/**
 * 원통 프레임 폭 mm — 사진 한 장에 보이는 반둘레(ADR-0007 D2). 지름은 products.width_mm 다.
 * 서버 실측 검사와 어드민이 같은 함수로 계산해 같은 double 을 만든다(반올림하지 않는다).
 */
export const cylinderFrameWidthMm = (diameterMm: number): number => (Math.PI * diameterMm) / 2;

/**
 * 원통 서피스 위 배치 위반(ADR-0007 D9).
 * - WIDER_THAN_CIRCUMFERENCE: 컴포넌트 폭이 둘레(프레임 폭 × 2)보다 크다.
 * - OUTSIDE_FRAME_HEIGHT: 세로가 프레임 밖으로 나간다.
 * - NO_HORIZONTAL_OVERLAP: 프레임과 가로로 겹치지 않는다.
 */
export type CylinderPlacementIssue =
  | 'WIDER_THAN_CIRCUMFERENCE'
  | 'OUTSIDE_FRAME_HEIGHT'
  | 'NO_HORIZONTAL_OVERLAP';

/**
 * 원통 서피스 위 배치를 검사한다. 가로는 프레임과 겹치기만 하면 되므로 음수 위치도 받는다.
 * 세로는 프레임 안에 모두 들어가야 한다. 위반이 없으면 null.
 */
export const cylinderPlacementIssue = (
  component: SizeMm,
  frame: SizeMm,
  position: PlacementPosition,
): CylinderPlacementIssue | null => {
  if (component.widthMm > 2 * frame.widthMm + GEOMETRY_TOLERANCE) {
    return 'WIDER_THAN_CIRCUMFERENCE';
  }
  if (
    position.yMm < -GEOMETRY_TOLERANCE ||
    position.yMm + component.heightMm > frame.heightMm + GEOMETRY_TOLERANCE
  ) {
    return 'OUTSIDE_FRAME_HEIGHT';
  }
  const overlap =
    Math.min(position.xMm + component.widthMm, frame.widthMm) - Math.max(position.xMm, 0);

  return overlap > GEOMETRY_TOLERANCE ? null : 'NO_HORIZONTAL_OVERLAP';
};

export interface VerticalExtent {
  readonly top: number;
  readonly bottom: number;
}

/**
 * 곡선 양 끝까지 포함한 투영 세로 범위(사진 %). 양 끝이 영역 밖으로 나갈 때만 넓어진다
 * (위 곡선이 위로 솟거나 아래 곡선이 아래로 처질 때). 엔진의 enCylinderizeSize 와 같은 규칙이다.
 */
export const cylinderVerticalExtent = (
  points: FlatSurfacePoints,
  topCurveRatio: number,
  bottomCurveRatio: number,
): VerticalExtent => {
  const [topLeft, , , bottomLeft] = points;
  const height = bottomLeft.y - topLeft.y;

  return {
    top: topLeft.y + Math.min(0, topCurveRatio) * height,
    bottom: bottomLeft.y + Math.max(0, bottomCurveRatio) * height,
  };
};

// ---------------------------------------------------------------------------
// 윤곽(outline) — 운영자가 사진 위 제품 경계를 따라 찍은 닫힌 다각형(사진 %). 첫 점을 반복 저장하지 않는다.
// flat 서피스의 제어점 4개는 윤곽을 감싸는 틀이라 축척 식은 윤곽이 없을 때와 같다 — 틀이 곧 실측 mm 프레임이다.
// ---------------------------------------------------------------------------

export interface OutlinedSurfaceLike {
  readonly realSize: SizeMm;
  /** TL·TR·BR·BL. 윤곽을 감싸는 틀(실측 mm 프레임)이다. */
  readonly points: FlatSurfacePoints;
  readonly outline: { readonly points: readonly SurfacePoint[] };
}

const vertexAt = (points: readonly SurfacePoint[], index: number): SurfacePoint => {
  const point = points[index % points.length];
  if (point === undefined) {
    throw new RangeError(`윤곽 점 ${index} 가 없습니다.`);
  }
  return point;
};

const signOf = (value: number): -1 | 0 | 1 =>
  Math.abs(value) <= GEOMETRY_TOLERANCE ? 0 : value > 0 ? 1 : -1;

/** c 가 직선 a→b 의 어느 쪽에 있는가. 0 이면 한 줄이다. */
const sideOf = (a: SurfacePoint, b: SurfacePoint, c: SurfacePoint): -1 | 0 | 1 =>
  signOf((b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x));

/** a→b 와 한 줄에 있는 p 가 선분 a-b 범위 안인가. */
const isWithinSegment = (a: SurfacePoint, b: SurfacePoint, p: SurfacePoint): boolean =>
  p.x >= Math.min(a.x, b.x) - GEOMETRY_TOLERANCE &&
  p.x <= Math.max(a.x, b.x) + GEOMETRY_TOLERANCE &&
  p.y >= Math.min(a.y, b.y) - GEOMETRY_TOLERANCE &&
  p.y <= Math.max(a.y, b.y) + GEOMETRY_TOLERANCE;

/** 두 선분이 서로의 안쪽에서 가로지르는가(끝점 접촉·겹침은 제외). */
const segmentsCross = (
  a: SurfacePoint,
  b: SurfacePoint,
  c: SurfacePoint,
  d: SurfacePoint,
): boolean => sideOf(a, b, c) * sideOf(a, b, d) < 0 && sideOf(c, d, a) * sideOf(c, d, b) < 0;

/** 두 선분이 한 점이라도 공유하는가(가로지름·끝점 접촉·겹침 포함). */
const segmentsTouch = (
  a: SurfacePoint,
  b: SurfacePoint,
  c: SurfacePoint,
  d: SurfacePoint,
): boolean =>
  segmentsCross(a, b, c, d) ||
  (sideOf(a, b, c) === 0 && isWithinSegment(a, b, c)) ||
  (sideOf(a, b, d) === 0 && isWithinSegment(a, b, d)) ||
  (sideOf(c, d, a) === 0 && isWithinSegment(c, d, a)) ||
  (sideOf(c, d, b) === 0 && isWithinSegment(c, d, b));

/** 점들의 외접 상자. */
interface Box {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

const boxOf = (points: readonly SurfacePoint[]): Box => {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const point of points) {
    if (point.x < minX) minX = point.x;
    if (point.x > maxX) maxX = point.x;
    if (point.y < minY) minY = point.y;
    if (point.y > maxY) maxY = point.y;
  }
  return { minX, minY, maxX, maxY };
};

/** 두 상자가 허용오차까지 넓혀 겹치는가. */
const boxesOverlap = (a: Box, b: Box): boolean =>
  a.minX <= b.maxX + GEOMETRY_TOLERANCE &&
  b.minX <= a.maxX + GEOMETRY_TOLERANCE &&
  a.minY <= b.maxY + GEOMETRY_TOLERANCE &&
  b.minY <= a.maxY + GEOMETRY_TOLERANCE;

/**
 * 고리 변 색인 — 변을 y 띠로 나눠, 점·선분 근처의 변만 훑는다. 변은 y 범위를 허용오차만큼 넓혀 겹치는 띠마다 넣으므로,
 * 한 점(또는 선분)과 닿거나 가로지를 수 있는 변은 모두 그 점(선분)의 띠에 있다 — 판정 결과는 모든 변을 보는 것과 같다.
 * 곡선을 풀어 쓴 고리(수천 점)끼리의 검사가 변 수의 곱만큼 느려지지 않게 한다.
 */
interface RingIndex {
  readonly ring: readonly SurfacePoint[];
  readonly box: Box;
  /** 변 i(점 i → 점 i+1)의 외접 상자. */
  readonly edgeBoxes: readonly Box[];
  readonly bandHeight: number;
  /** 띠마다 그 띠와 겹치는 변 번호(오름차순). */
  readonly bands: readonly (readonly number[])[];
}

/** 띠 수 상한 — 점이 많아도 색인을 만드는 비용이 점 수에 비례하게 둔다. */
const MAX_RING_BANDS = 256;

const bandOf = (index: Pick<RingIndex, 'box' | 'bandHeight' | 'bands'>, y: number): number => {
  if (!(index.bandHeight > 0)) return 0;
  const band = Math.floor((y - index.box.minY) / index.bandHeight);
  return Math.min(index.bands.length - 1, Math.max(0, band));
};

const indexRing = (ring: readonly SurfacePoint[]): RingIndex => {
  const box = boxOf(ring);
  const height = box.maxY - box.minY;
  const bandCount =
    height > GEOMETRY_TOLERANCE
      ? Math.max(1, Math.min(MAX_RING_BANDS, Math.ceil(Math.sqrt(ring.length))))
      : 1;
  const bands: number[][] = Array.from({ length: bandCount }, () => []);
  const index = { ring, box, bandHeight: height / bandCount, bands };
  const edgeBoxes = ring.map((a, i) => {
    const b = vertexAt(ring, i + 1);
    const edgeBox = {
      minX: Math.min(a.x, b.x),
      minY: Math.min(a.y, b.y),
      maxX: Math.max(a.x, b.x),
      maxY: Math.max(a.y, b.y),
    };
    const from = bandOf(index, edgeBox.minY - GEOMETRY_TOLERANCE);
    const to = bandOf(index, edgeBox.maxY + GEOMETRY_TOLERANCE);
    for (let band = from; band <= to; band += 1) bands[band]?.push(i);
    return edgeBox;
  });
  return { ...index, edgeBoxes };
};

/**
 * 상자와 겹칠 수 있는 변 번호마다 visit 을 부른다(같은 변을 여러 번 부를 수 있다). visit 이 true 를 돌려주면 멈추고 true.
 */
const someEdgeNear = (index: RingIndex, box: Box, visit: (edge: number) => boolean): boolean => {
  if (!boxesOverlap(box, index.box)) return false;
  const from = bandOf(index, box.minY - GEOMETRY_TOLERANCE);
  const to = bandOf(index, box.maxY + GEOMETRY_TOLERANCE);
  for (let band = from; band <= to; band += 1) {
    for (const edge of index.bands[band] ?? []) {
      const edgeBox = index.edgeBoxes[edge];
      if (edgeBox !== undefined && boxesOverlap(box, edgeBox) && visit(edge)) return true;
    }
  }
  return false;
};

/** isPointInPolygon 과 같은 판정 — 점의 띠에 든 변만 본다(그 밖의 변은 반직선과 만나거나 점에 닿을 수 없다). */
const isPointInIndexedRing = (point: SurfacePoint, index: RingIndex): boolean => {
  const { box, ring } = index;
  if (
    point.x < box.minX - GEOMETRY_TOLERANCE ||
    point.x > box.maxX + GEOMETRY_TOLERANCE ||
    point.y < box.minY - GEOMETRY_TOLERANCE ||
    point.y > box.maxY + GEOMETRY_TOLERANCE
  )
    return false;
  let inside = false;
  for (const edge of index.bands[bandOf(index, point.y)] ?? []) {
    const a = vertexAt(ring, edge);
    const b = vertexAt(ring, edge + 1);
    if (sideOf(a, b, point) === 0 && isWithinSegment(a, b, point)) return true;
    if (a.y > point.y !== b.y > point.y) {
      const crossingX = a.x + ((point.y - a.y) * (b.x - a.x)) / (b.y - a.y);
      if (crossingX > point.x) inside = !inside;
    }
  }
  return inside;
};

/** 두 점 배열이 같은 길이이고 점마다 허용오차 안에서 같은가. */
export const arePointsNear = (a: readonly SurfacePoint[], b: readonly SurfacePoint[]): boolean =>
  a.length === b.length &&
  a.every((point, index) => {
    const other = b[index];
    return other !== undefined && isNear(point.x, other.x) && isNear(point.y, other.y);
  });

/** 다각형의 부호 없는 넓이(신발끈 공식). 단위는 입력 좌표의 제곱이다. */
export const polygonArea = (points: readonly SurfacePoint[]): number => {
  const twice = points.reduce((sum, point, index) => {
    const next = vertexAt(points, index + 1);
    return sum + point.x * next.y - next.x * point.y;
  }, 0);

  return Math.abs(twice) / 2;
};

/**
 * 축 정렬 사각형(TL·TR·BR·BL)이 윤곽을 허용오차 안에서 모두 감싸는가 — flat 서피스의 틀(points) 검사.
 * 틀은 윤곽의 외접 사각형과 같거나 그보다 넓을 수 있다(실측 비율에 맞춘 틀). 윤곽을 자르는 틀은 거부한다.
 */
export const rectContainsOutline = (
  rect: FlatSurfacePoints,
  outline: readonly SurfacePoint[],
): boolean => {
  const [tl, , br] = rect;
  return outline.every(
    (point) =>
      point.x >= tl.x - GEOMETRY_TOLERANCE &&
      point.x <= br.x + GEOMETRY_TOLERANCE &&
      point.y >= tl.y - GEOMETRY_TOLERANCE &&
      point.y <= br.y + GEOMETRY_TOLERANCE,
  );
};

/** 윤곽을 감싸는 가장 작은 축 정렬 직사각형(TL·TR·BR·BL). 점이 1개 이상이어야 한다 — 개수 검사는 zod 책임이다. */
export const outlineBounds = (points: readonly SurfacePoint[]): FlatSurfacePoints => {
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const left = Math.min(...xs);
  const right = Math.max(...xs);
  const top = Math.min(...ys);
  const bottom = Math.max(...ys);

  return [
    { x: left, y: top },
    { x: right, y: top },
    { x: right, y: bottom },
    { x: left, y: bottom },
  ];
};

/**
 * 닫힌 경로가 스스로 교차하지 않는 다각형인가. 점 3개 미만, 길이 0 인 변, 바로 되접히는 변,
 * 이웃하지 않은 두 변의 접촉·교차를 거부한다.
 */
export const isSimplePolygon = (points: readonly SurfacePoint[]): boolean => {
  const count = points.length;
  if (count < 3) return false;

  for (let i = 0; i < count; i += 1) {
    const a = vertexAt(points, i);
    const b = vertexAt(points, i + 1);
    const c = vertexAt(points, i + 2);

    if (isNear(a.x, b.x) && isNear(a.y, b.y)) return false;
    const foldsBack = (a.x - b.x) * (c.x - b.x) + (a.y - b.y) * (c.y - b.y) > 0;
    if (sideOf(a, b, c) === 0 && foldsBack) return false;
  }

  // 이웃하지 않은 두 변(i < j, 첫 변과 마지막 변 제외)의 접촉 — 닿을 수 있는 변은 같은 띠·겹치는 상자에만 있다.
  const index = indexRing(points);
  return !index.edgeBoxes.some((edgeBox, i) => {
    const a = vertexAt(points, i);
    const b = vertexAt(points, i + 1);
    return someEdgeNear(index, edgeBox, (j) => {
      if (j < i + 2 || (i === 0 && j === count - 1)) return false;
      return segmentsTouch(a, b, vertexAt(points, j), vertexAt(points, j + 1));
    });
  });
};

/** 점이 다각형 안에 있는가. 변 위와 꼭짓점은 안으로 본다(경계 접촉 허용). */
export const isPointInPolygon = (
  point: SurfacePoint,
  polygon: readonly SurfacePoint[],
): boolean => {
  let inside = false;

  for (let i = 0; i < polygon.length; i += 1) {
    const a = vertexAt(polygon, i);
    const b = vertexAt(polygon, i + 1);

    if (sideOf(a, b, point) === 0 && isWithinSegment(a, b, point)) return true;
    if (a.y > point.y !== b.y > point.y) {
      const crossingX = a.x + ((point.y - a.y) * (b.x - a.x)) / (b.y - a.y);
      if (crossingX > point.x) inside = !inside;
    }
  }

  return inside;
};

/** 점에서 선분 a-b 까지의 거리. */
const distanceToSegment = (point: SurfacePoint, a: SurfacePoint, b: SurfacePoint): number => {
  const deltaX = b.x - a.x;
  const deltaY = b.y - a.y;
  const lengthSquared = deltaX * deltaX + deltaY * deltaY;
  const projected =
    lengthSquared === 0 ? 0 : ((point.x - a.x) * deltaX + (point.y - a.y) * deltaY) / lengthSquared;
  const t = Math.min(1, Math.max(0, projected));

  return Math.hypot(point.x - (a.x + t * deltaX), point.y - (a.y + t * deltaY));
};

/** 점이 고리의 열린 내부(안이고 모든 변에서 허용오차보다 멀리)에 있는가. */
const isStrictlyInsideRing = (point: SurfacePoint, ring: readonly SurfacePoint[]): boolean =>
  isPointInPolygon(point, ring) &&
  ring.every(
    (a, index) => distanceToSegment(point, a, vertexAt(ring, index + 1)) > GEOMETRY_TOLERANCE,
  );

/**
 * 윤곽 변 a-b 가 고리의 열린 내부를 지나는가. 고리의 어떤 변과 제대로 가로지르면(끝점 접촉·공선 겹침 제외) 지난다.
 * 그렇지 않으면 a-b 를 그 위에 놓인 고리 꼭짓점에서 쪼갠다(t∈[0,1], 양 끝 포함) — 이웃한 분할점 사이의 열린 조각은
 * 고리 경계와 만나지 않으므로 통째로 안이거나 밖이고, 길이가 허용오차를 넘는 조각의 중점이 고리의 열린 내부에 있으면 지난다.
 * 변 전체의 중점 하나만 보면 꼭짓점에서만 닿으며(T자 포함) 안으로 드나드는 변을 놓친다.
 * 고리 변과 겹치기만 하는 조각은 중점이 경계 위라 지나지 않는다.
 */
const crossesPolygonInterior = (
  a: SurfacePoint,
  b: SurfacePoint,
  ring: readonly SurfacePoint[],
): boolean => {
  if (ring.some((c, index) => segmentsCross(a, b, c, vertexAt(ring, index + 1)))) return true;

  const deltaX = b.x - a.x;
  const deltaY = b.y - a.y;
  const length = Math.hypot(deltaX, deltaY);
  if (length <= GEOMETRY_TOLERANCE) return false;
  const lengthSquared = length * length;
  const cuts = [
    0,
    1,
    ...ring
      .filter((vertex) => sideOf(a, b, vertex) === 0 && isWithinSegment(a, b, vertex))
      .map((vertex) => ((vertex.x - a.x) * deltaX + (vertex.y - a.y) * deltaY) / lengthSquared),
  ].sort((first, second) => first - second);

  return cuts.some((from, index) => {
    const to = cuts[index + 1];
    if (to === undefined || (to - from) * length <= GEOMETRY_TOLERANCE) return false;
    const middle = (from + to) / 2;
    return isStrictlyInsideRing({ x: a.x + deltaX * middle, y: a.y + deltaY * middle }, ring);
  });
};

/**
 * 고리의 열린 내부에 반드시 드는 한 점. 무게중심은 오목한 고리(C자 등)에서 고리 밖에 놓일 수 있어 쓰지 않는다.
 * 꼭짓점 y 값 사이 가장 넓은 틈의 가운데에 수평선을 긋고, 그 선이 고리를 자른 구간 중 가장 긴 것의 가운데를 고른다.
 * 선이 꼭짓점을 지나지 않으므로 교차점이 짝수 개이고 (0·1), (2·3)… 이 고리 안 구간이다. 넓이 없는 고리면 null.
 */
const interiorPointOf = (ring: readonly SurfacePoint[]): SurfacePoint | null => {
  const ys = [...new Set(ring.map((point) => point.y))].sort((a, b) => a - b);
  let lineY = 0;
  let widestGap = 0;
  ys.forEach((y, index) => {
    const previous = ys[index - 1];
    if (previous !== undefined && y - previous > widestGap) {
      widestGap = y - previous;
      lineY = (previous + y) / 2;
    }
  });
  if (widestGap <= GEOMETRY_TOLERANCE) return null;

  const crossings = ring
    .flatMap((a, index) => {
      const b = vertexAt(ring, index + 1);
      return a.y > lineY !== b.y > lineY ? [a.x + ((lineY - a.y) * (b.x - a.x)) / (b.y - a.y)] : [];
    })
    .sort((a, b) => a - b);

  let best: SurfacePoint | null = null;
  let widestSpan = GEOMETRY_TOLERANCE;
  for (let index = 0; index + 1 < crossings.length; index += 2) {
    const from = crossings[index];
    const to = crossings[index + 1];
    if (from !== undefined && to !== undefined && to - from > widestSpan) {
      widestSpan = to - from;
      best = { x: (from + to) / 2, y: lineY };
    }
  }

  return best;
};

/**
 * 배치(컴포넌트 모양 × 서피스 안 위치)를 flat 매핑으로 사진 % 에 옮긴 모양 고리(shapeRingOf)가 윤곽 안에 있는가.
 * ① 고리의 모든 점이 윤곽 안(경계 포함) ② 윤곽의 어느 변도 고리의 열린 내부를 지나지 않음 — 고리 변과 제대로
 * 가로지르지 않고, 변 위의 고리 꼭짓점에서 쪼갠 조각마다 중점이 고리의 열린 내부에 없어야 한다(crossesPolygonInterior).
 * mm 수치끼리가 아니라 투영된 모양으로 비교한다. 사각형(shape 없음)은 네 모서리 고리라 기존 판정과 같다.
 * ② 가 성립하면 윤곽 경계가 고리의 열린 내부를 가르지 못하므로 그 내부는 통째로 윤곽 안이거나 통째로 밖이다 —
 * 홈의 벽·바닥과 정확히 겹치는 고리(홈을 딱 채운 배치)는 ①·② 를 통과하므로, 고리 안에 반드시 드는 한 점
 * (interiorPointOf — 오목한 모양에서 밖에 놓일 수 있는 무게중심 대신)으로 어느 쪽인지 가른다.
 * 구멍은 보지 않는다 — 구멍은 인쇄하지 않을 뿐 배치가 차지하는 범위는 바깥 고리다.
 */
export const isPlacementInsideOutline = (
  component: ShapedComponent,
  surface: OutlinedSurfaceLike,
  position: PlacementPosition,
): boolean => {
  const [topLeft, , bottomRight] = surface.points;
  const ring = shapeRingOf(component).map(
    (point): SurfacePoint => ({
      x:
        topLeft.x +
        ((position.xMm + point.xMm) / surface.realSize.widthMm) * (bottomRight.x - topLeft.x),
      y:
        topLeft.y +
        ((position.yMm + point.yMm) / surface.realSize.heightMm) * (bottomRight.y - topLeft.y),
    }),
  );
  const outline = surface.outline.points;

  if (!ring.every((point) => isPointInPolygon(point, outline))) return false;

  const passesThrough = outline.some((vertex, index) =>
    crossesPolygonInterior(vertex, vertexAt(outline, index + 1), ring),
  );
  if (passesThrough) return false;

  // null 은 넓이 없는 고리뿐이다 — 퇴화 다각형은 componentShapeIssue 가 따로 거부하므로 여기서는 통과시킨다.
  const inner = interiorPointOf(ring);
  return inner === null || isPointInPolygon(inner, outline);
};

// ---------------------------------------------------------------------------
// 정의 전체의 교차 검사 — id 유일성 · 참조 무결성 · 배치 ⊂ 서피스(원통은 겹침) · 색상 면 키 집합 · 완전성.
// 구조 검사(필수 필드·범위)는 zod 가 먼저 끝내므로 여기서는 관계만 본다.
// ---------------------------------------------------------------------------

export interface DefinitionIssue {
  readonly path: readonly (string | number)[];
  readonly message: string;
}

// ---------------------------------------------------------------------------
// 인쇄 영역 모양(shape) — 컴포넌트 좌상단 기준 mm. 사각형(shape 없음)·원형·자유 다각형(+구멍).
// ---------------------------------------------------------------------------

export type ShapedComponent = SizeMm & { readonly shape?: ComponentShape };

/** editor.ts 의 ELLIPSE_SAMPLE_POINTS 와 같은 값 — 런타임 순환 import 를 피해 여기 둔다(spec 이 일치를 확인). */
const ELLIPSE_RING_POINTS = 64;

const toXY = (point: ShapePoint): SurfacePoint => ({ x: point.xMm, y: point.yMm });

/** 곡선 구간(3차 베지어) 하나를 풀어 쓰는 선분 수. */
export const BEZIER_SEGMENT_STEPS = 16;

const hasHandle = (point: ShapeVertex): boolean =>
  point.handleIn !== undefined || point.handleOut !== undefined;

/**
 * 자유 다각형 고리(바깥 고리·구멍 고리)를 직선 고리로 푼다. 구간 i→i+1(마지막→첫 점 포함)은 pᵢ.handleOut 이나 pᵢ₊₁.handleIn 이
 * 하나라도 있으면 3차 베지어(없는 제어점은 그 끝점), 아니면 직선이다. 베지어 구간은 끝점을 뺀 16점
 * (t = 0, 1/16, …, 15/16)을 내고, 끝점은 다음 구간이 낸다. 결과 점에는 손잡이가 없다.
 */
export const flattenPolygonRing = (points: readonly ShapeVertex[]): ShapePoint[] => {
  const flat: ShapePoint[] = [];
  points.forEach((start, index) => {
    flat.push({ xMm: start.xMm, yMm: start.yMm });
    const end = points[(index + 1) % points.length];
    if (end === undefined || (start.handleOut === undefined && end.handleIn === undefined)) return;
    const c1 = start.handleOut ?? start;
    const c2 = end.handleIn ?? end;
    for (let step = 1; step < BEZIER_SEGMENT_STEPS; step += 1) {
      const t = step / BEZIER_SEGMENT_STEPS;
      const u = 1 - t;
      const a = u * u * u;
      const b = 3 * u * u * t;
      const c = 3 * u * t * t;
      const d = t * t * t;
      flat.push({
        xMm: a * start.xMm + b * c1.xMm + c * c2.xMm + d * end.xMm,
        yMm: a * start.yMm + b * c1.yMm + c * c2.yMm + d * end.yMm,
      });
    }
  });
  return flat;
};

/** 같은 점 배열(불변)이면 다시 풀지 않는다. */
const flattenedRings = new WeakMap<readonly ShapeVertex[], readonly ShapePoint[]>();

const flattenedRingOf = (points: readonly ShapeVertex[]): readonly ShapePoint[] => {
  // 손잡이가 하나도 없으면 직선 고리 그대로 — 기존 데이터의 결과·객체가 바뀌지 않는다.
  if (!points.some(hasHandle)) return points;
  const cached = flattenedRings.get(points);
  if (cached !== undefined) return cached;
  const flat = flattenPolygonRing(points);
  flattenedRings.set(points, flat);
  return flat;
};

/**
 * 모양의 바깥 고리. 사각형은 TL·TR·BR·BL, 원형은 64점 근사, 다각형은 그린 순서 그대로(곡선 구간은 풀어 쓴 점).
 * 마스크·윤곽 안 검사·외접 사각형·단순성 검사·오버레이가 모두 이 고리를 본다.
 */
export const shapeRingOf = (component: ShapedComponent): readonly ShapePoint[] => {
  const { shape, widthMm, heightMm } = component;
  if (shape?.type === 'polygon') return flattenedRingOf(shape.points);
  if (shape?.type === 'ellipse') {
    return Array.from({ length: ELLIPSE_RING_POINTS }, (_, index) => {
      const angle = (2 * Math.PI * index) / ELLIPSE_RING_POINTS;
      return {
        xMm: widthMm / 2 + (widthMm / 2) * Math.cos(angle),
        yMm: heightMm / 2 + (heightMm / 2) * Math.sin(angle),
      };
    });
  }
  return [
    { xMm: 0, yMm: 0 },
    { xMm: widthMm, yMm: 0 },
    { xMm: widthMm, yMm: heightMm },
    { xMm: 0, yMm: heightMm },
  ];
};

/** 구멍 고리들. 곡선 구간은 바깥 고리와 같이 풀어 쓴 점이다 — 마스크·검사·오버레이가 모두 이 고리를 본다. */
export const shapeHolesOf = (component: ShapedComponent): readonly (readonly ShapePoint[])[] =>
  component.shape?.type === 'polygon' ? (component.shape.holes ?? []).map(flattenedRingOf) : [];

const isOnRingBoundary = (point: SurfacePoint, ring: readonly SurfacePoint[]): boolean =>
  ring.some((a, index) => {
    const b = vertexAt(ring, index + 1);
    return sideOf(a, b, point) === 0 && isWithinSegment(a, b, point);
  });

/**
 * 컴포넌트 외접 사각형에 내접한 타원식 판정 — 경계 포함(허용오차 GEOMETRY_TOLERANCE).
 * 64점 고리(shapeRingOf)는 그리기·윤곽 검사용이고, 마스크·히트테스트는 이 식을 쓴다.
 */
const isPointInEllipse = (component: SizeMm, point: ShapePoint): boolean => {
  const rx = component.widthMm / 2;
  const ry = component.heightMm / 2;
  if (rx <= 0 || ry <= 0) return false;
  const dx = (point.xMm - rx) / rx;
  const dy = (point.yMm - ry) / ry;
  return dx * dx + dy * dy <= 1 + GEOMETRY_TOLERANCE;
};

/** 점이 인쇄되는 영역 안인가. 바깥 고리 경계는 포함, 구멍은 안쪽만 제외(구멍 경계는 영역에 속한다). */
export const isPointInShape = (component: ShapedComponent, point: ShapePoint): boolean => {
  if (component.shape?.type === 'ellipse') return isPointInEllipse(component, point);
  const target = toXY(point);
  if (!isPointInPolygon(target, shapeRingOf(component).map(toXY))) return false;

  return !shapeHolesOf(component).some((hole) => {
    const ring = hole.map(toXY);
    return isPointInPolygon(target, ring) && !isOnRingBoundary(target, ring);
  });
};

/** 점들의 외접 사각형. 점이 1개 이상이어야 한다. */
export const shapeBoundsOf = (
  points: readonly ShapePoint[],
): { left: number; top: number; widthMm: number; heightMm: number } => {
  const xs = points.map((point) => point.xMm);
  const ys = points.map((point) => point.yMm);
  const left = Math.min(...xs);
  const top = Math.min(...ys);

  return { left, top, widthMm: Math.max(...xs) - left, heightMm: Math.max(...ys) - top };
};

const isRingProper = (ring: readonly SurfacePoint[]): boolean =>
  isSimplePolygon(ring) && polygonArea(ring) > GEOMETRY_TOLERANCE;

/** 고리 a 의 어떤 변이 색인한 고리 b 의 변과 제대로 가로지르는가. 상자가 겹치는 변 쌍만 본다. */
const ringsCross = (a: readonly SurfacePoint[], b: RingIndex): boolean =>
  a.some((p, i) => {
    const q = vertexAt(a, i + 1);
    const segmentBox = {
      minX: Math.min(p.x, q.x),
      minY: Math.min(p.y, q.y),
      maxX: Math.max(p.x, q.x),
      maxY: Math.max(p.y, q.y),
    };
    return someEdgeNear(b, segmentBox, (j) =>
      segmentsCross(p, q, vertexAt(b.ring, j), vertexAt(b.ring, j + 1)),
    );
  });

/** 풀어 쓴 고리(바깥 고리 + 구멍들) 점의 합 상한 — 곡선 점·구멍을 많이 쓰면 검사·마스크 비용이 커진다. */
export const MAX_FLATTENED_SHAPE_POINTS = 8192;

const MESSAGE_RING = '인쇄 영역 모양이 스스로 교차하거나 넓이가 없습니다.';
const MESSAGE_RANGE = '인쇄 영역 모양의 점이 가로·세로 범위를 벗어납니다.';
const MESSAGE_BOUNDS = '인쇄 영역 모양의 외접 사각형이 가로·세로와 다릅니다.';
const MESSAGE_HOLE_RING = '인쇄 제외 영역이 스스로 교차하거나 넓이가 없습니다.';
const MESSAGE_HOLE_OUTSIDE = '인쇄 제외 영역이 인쇄 영역 바깥을 벗어납니다.';
const MESSAGE_HOLE_OVERLAP = '인쇄 제외 영역끼리 겹칩니다.';
const MESSAGE_TOO_COMPLEX = `인쇄 영역 모양이 너무 복잡합니다(곡선을 풀어 쓴 점 최대 ${MAX_FLATTENED_SHAPE_POINTS}개). 곡선 점이나 인쇄 제외 영역을 줄여 주세요.`;

/** 모양 자체의 위반 사유(한국어). 위반이 없거나 사각형·원형이면 null. */
export const componentShapeIssue = (component: ShapedComponent): string | null => {
  const { shape } = component;
  if (shape?.type !== 'polygon') return null;

  // 곡선 구간은 풀어 쓴 고리로 검사한다 — 손잡이 점 자체는 범위·외접 사각형 밖이어도 된다.
  const flat = shapeRingOf(component);
  const flatHoles = shapeHolesOf(component);
  const flattenedCount = flatHoles.reduce((sum, hole) => sum + hole.length, flat.length);
  if (flattenedCount > MAX_FLATTENED_SHAPE_POINTS) return MESSAGE_TOO_COMPLEX;
  const ring = flat.map(toXY);
  if (!isRingProper(ring)) return MESSAGE_RING;

  const within = (p: ShapePoint): boolean =>
    p.xMm >= -GEOMETRY_TOLERANCE &&
    p.xMm <= component.widthMm + GEOMETRY_TOLERANCE &&
    p.yMm >= -GEOMETRY_TOLERANCE &&
    p.yMm <= component.heightMm + GEOMETRY_TOLERANCE;
  if (!flat.every(within)) return MESSAGE_RANGE;

  const bounds = shapeBoundsOf(flat);
  if (
    !isNear(bounds.left, 0) ||
    !isNear(bounds.top, 0) ||
    !isNear(bounds.widthMm, component.widthMm) ||
    !isNear(bounds.heightMm, component.heightMm)
  ) {
    return MESSAGE_BOUNDS;
  }

  const holes = flatHoles.map((hole) => hole.map(toXY));
  if (!holes.every(isRingProper)) return MESSAGE_HOLE_RING;
  if (holes.length === 0) return null;
  const outerArea = polygonArea(ring);
  const outer = indexRing(ring);
  const isHoleOutside = (hole: readonly SurfacePoint[]): boolean =>
    !hole.every((p) => isPointInIndexedRing(p, outer)) ||
    ringsCross(hole, outer) ||
    // 변 중점이 오목한 홈 바깥을 지나는 경우를 잡는다.
    hole.some((a, i) => {
      const b = vertexAt(hole, i + 1);
      return !isPointInIndexedRing({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, outer);
    }) ||
    // 구멍 = 바깥 고리(인쇄 가능 영역 0)를 잡는다. 위 검사를 통과한 구멍은 바깥 고리 안이라 넓이가 같으면 같은 모양이다.
    polygonArea(hole) > outerArea - GEOMETRY_TOLERANCE;
  if (holes.some(isHoleOutside)) return MESSAGE_HOLE_OUTSIDE;

  // 구멍끼리는 한 점·한 변만 닿아도 겹침으로 본다(바깥 고리와 닿는 것은 허용하는 것과 비대칭).
  // 외접 상자가 떨어진 두 구멍은 닿을 수 없어 건너뛴다.
  const indexed = holes.map(indexRing);
  const overlaps = indexed.some((hole, i) =>
    indexed.some(
      (other, j) =>
        j > i &&
        boxesOverlap(hole.box, other.box) &&
        (ringsCross(hole.ring, other) ||
          hole.ring.some((p) => isPointInIndexedRing(p, other)) ||
          other.ring.some((p) => isPointInIndexedRing(p, hole))),
    ),
  );
  return overlaps ? MESSAGE_HOLE_OVERLAP : null;
};

interface ComponentLike extends ShapedComponent {
  readonly id: string;
  readonly name: string;
}

interface SurfaceLikeBase {
  readonly id: string;
  readonly realSize: SizeMm;
  readonly outline?: { readonly points: readonly SurfacePoint[] };
}

/** 계약의 `FlatSurface` 가 그대로 할당된다 — 제어점 4개는 윤곽을 감싸는 틀(TL·TR·BR·BL)이다. */
interface FlatSurfaceLike extends SurfaceLikeBase {
  readonly type: 'flat';
  readonly points: FlatSurfacePoints;
}

/** 계약의 `MeshSurface` 가 그대로 할당된다 — 제어점은 columns×rows 격자다. */
interface MeshSurfaceLike extends SurfaceLikeBase {
  readonly type: 'mesh';
  readonly points: readonly SurfacePoint[];
}

/** 계약의 `CylinderSurface` 가 그대로 할당된다 — 제어점 4개는 사진에서 보이는 원통 사각형(TL·TR·BR·BL)이다. */
interface CylinderSurfaceLike extends SurfaceLikeBase {
  readonly type: 'cylinder';
  readonly points: FlatSurfacePoints;
  readonly topCurveRatio: number;
  readonly bottomCurveRatio: number;
}

/**
 * 판별 유니온으로 둔다 — `type` 이 넓은 `string` 이면 계약의 판별자가 바뀌거나 오타가 나도
 * 컴파일되고 윤곽 포함 검사가 조용히 꺼진다.
 */
type SurfaceLike = FlatSurfaceLike | MeshSurfaceLike | CylinderSurfaceLike;

interface PlacementLike {
  readonly componentId: string;
  readonly surfaceId: string;
  readonly position: PlacementPosition;
}

interface PixelSize {
  readonly widthPx: number;
  readonly heightPx: number;
}

/** 계약의 `FaceComposite` 가 그대로 할당된다 — 크기만 본다. */
interface CompositeLike {
  readonly maskSize?: PixelSize;
  readonly shadingSize?: PixelSize;
  readonly safeAreaSize?: PixelSize;
}

interface FaceLike {
  readonly surfaces: readonly SurfaceLike[];
  readonly placements: readonly PlacementLike[];
  readonly composite?: CompositeLike;
}

interface ColorLike {
  readonly colorOptionId: string;
  readonly faces: Readonly<Record<string, { readonly photoSize: PixelSize } | undefined>>;
}

export interface DefinitionLike {
  readonly components: readonly ComponentLike[];
  readonly faces: Readonly<Record<string, FaceLike | undefined>>;
  readonly colors: readonly ColorLike[];
}

/** 면 키의 검사 순서 — 앞·뒤·좌·우. 위반 경로가 정의 순서대로 보고되게 한다. */
const FACE_ORDER: readonly string[] = ['front', 'back', 'left', 'right'];

const faceRank = (key: string): number => {
  const rank = FACE_ORDER.indexOf(key);
  return rank === -1 ? FACE_ORDER.length : rank;
};

/** 값이 있는 키만, 앞·뒤·좌·우 순으로. */
const definedKeys = (record: Readonly<Record<string, unknown>>): readonly string[] =>
  Object.keys(record)
    .filter((key) => record[key] !== undefined)
    .sort((a, b) => faceRank(a) - faceRank(b) || a.localeCompare(b));

const collectComponentIssues = (
  components: readonly ComponentLike[],
  issues: DefinitionIssue[],
): ReadonlyMap<string, ComponentLike> => {
  const byId = new Map<string, ComponentLike>();
  const names = new Set<string>();

  components.forEach((component, index) => {
    if (byId.has(component.id)) {
      issues.push({ path: ['components', index, 'id'], message: '컴포넌트 id 가 중복됩니다.' });
    } else {
      byId.set(component.id, component);
    }

    const shapeIssue = componentShapeIssue(component);
    if (shapeIssue !== null) {
      issues.push({ path: ['components', index, 'shape'], message: shapeIssue });
    }

    const name = component.name.trim();
    if (names.has(name)) {
      issues.push({ path: ['components', index, 'name'], message: '컴포넌트 이름이 중복됩니다.' });
    }
    names.add(name);
  });

  return byId;
};

/**
 * 원통 배치 위반(cylinderPlacementIssue)의 한국어 문구. 저장 검사(superRefine)와 어드민 스토어의 배치 거부가
 * 같은 문장을 쓰도록 여기 한 곳에만 둔다.
 */
export const CYLINDER_PLACEMENT_MESSAGES: Readonly<Record<CylinderPlacementIssue, string>> = {
  WIDER_THAN_CIRCUMFERENCE: '컴포넌트 폭이 원통 둘레보다 큽니다.',
  OUTSIDE_FRAME_HEIGHT: '배치가 원통 프레임의 세로 범위를 벗어났습니다.',
  NO_HORIZONTAL_OVERLAP: '배치가 원통 프레임과 가로로 겹치지 않습니다.',
};

const collectFaceIssues = (
  faceKey: string,
  face: FaceLike,
  components: ReadonlyMap<string, ComponentLike>,
  surfaceIdsSeen: Set<string>,
  placedComponentIds: Set<string>,
  issues: DefinitionIssue[],
): void => {
  const surfacesById = new Map<string, SurfaceLike>();

  face.surfaces.forEach((surface, index) => {
    if (surfaceIdsSeen.has(surface.id)) {
      issues.push({
        path: ['faces', faceKey, 'surfaces', index, 'id'],
        message: '서피스 id 가 정의 안에서 중복됩니다.',
      });
      return;
    }
    surfaceIdsSeen.add(surface.id);
    surfacesById.set(surface.id, surface);
  });

  const placedInThisFace = new Set<string>();

  face.placements.forEach((placement, index) => {
    const path = ['faces', faceKey, 'placements', index] as const;
    const component = components.get(placement.componentId);
    const surface = surfacesById.get(placement.surfaceId);

    if (component === undefined) {
      issues.push({ path: [...path, 'componentId'], message: '정의에 없는 componentId 입니다.' });
    } else if (placedInThisFace.has(placement.componentId)) {
      issues.push({
        path: [...path, 'componentId'],
        message: '같은 면에 같은 컴포넌트를 두 번 배치할 수 없습니다.',
      });
    }
    placedInThisFace.add(placement.componentId);
    placedComponentIds.add(placement.componentId);

    if (surface === undefined) {
      issues.push({
        path: [...path, 'surfaceId'],
        message: '같은 면의 surfaces 에 없는 surfaceId 입니다.',
      });
    }

    if (component !== undefined && surface !== undefined && surface.type === 'cylinder') {
      const issue = cylinderPlacementIssue(component, surface.realSize, placement.position);
      if (issue !== null) {
        issues.push({ path: [...path, 'position'], message: CYLINDER_PLACEMENT_MESSAGES[issue] });
      }
    } else if (component !== undefined && surface !== undefined) {
      if (!isPlacementInsideSurface(component, surface.realSize, placement.position)) {
        issues.push({
          path: [...path, 'position'],
          message: '배치가 서피스 실측 범위를 벗어났습니다.',
        });
      } else if (
        surface.type === 'flat' &&
        surface.outline !== undefined &&
        !isPlacementInsideOutline(
          component,
          { realSize: surface.realSize, points: surface.points, outline: surface.outline },
          placement.position,
        )
      ) {
        issues.push({
          path: [...path, 'position'],
          message: '배치가 제품 윤곽을 벗어났습니다.',
        });
      }
    }
  });
};

const isSamePixelSize = (a: PixelSize, b: PixelSize): boolean =>
  a.widthPx === b.widthPx && a.heightPx === b.heightPx;

/**
 * 합성 에셋과 그 면의 모든 색상 사진은 마스크와 px 크기가 같아야 한다(ADR-0007 D6·D14).
 * 합성 함수가 사진·마스크·음영을 같은 px 격자로만 받기 때문이다.
 */
const collectCompositeIssues = (
  faceKey: string,
  composite: CompositeLike,
  colors: readonly ColorLike[],
  issues: DefinitionIssue[],
): void => {
  const path = ['faces', faceKey, 'composite'] as const;
  const maskSize = composite.maskSize;
  // 마스크가 없으면 비교 기준이 없다 — 곱하기만 켠 설정은 크기 검사를 건너뛴다.
  if (maskSize === undefined) return;

  if (composite.shadingSize !== undefined && !isSamePixelSize(composite.shadingSize, maskSize)) {
    issues.push({ path: [...path, 'shadingSize'], message: '음영 크기가 마스크 크기와 다릅니다.' });
  }
  if (composite.safeAreaSize !== undefined && !isSamePixelSize(composite.safeAreaSize, maskSize)) {
    issues.push({
      path: [...path, 'safeAreaSize'],
      message: '안내선 크기가 마스크 크기와 다릅니다.',
    });
  }
  colors.forEach((color, index) => {
    const photo = color.faces[faceKey];
    if (photo !== undefined && !isSamePixelSize(photo.photoSize, maskSize)) {
      issues.push({
        path: ['colors', index, 'faces', faceKey, 'photoSize'],
        message: '면 사진 크기가 마스크 크기와 다릅니다.',
      });
    }
  });
};

/**
 * 합성 설정은 cylinder 또는 mesh 서피스가 있는 면에만 둔다(ADR-0007 D6). 마스크는 선택이라
 * 원통 면에 합성 설정이 없어도, 곱하기만 켠 설정만 있어도 유효하다.
 */
const collectFaceCompositeIssues = (
  faceKey: string,
  face: FaceLike,
  colors: readonly ColorLike[],
  issues: DefinitionIssue[],
): void => {
  if (face.composite === undefined) return;
  const hasWarpSurface = face.surfaces.some(
    (surface) => surface.type === 'cylinder' || surface.type === 'mesh',
  );
  if (!hasWarpSurface) {
    issues.push({
      path: ['faces', faceKey, 'composite'],
      message: '합성 설정은 원통·격자 표면이 있는 면에만 둘 수 있습니다.',
    });
    return;
  }
  collectCompositeIssues(faceKey, face.composite, colors, issues);
};

/** 엔진(warpCylinder)은 투영 폭이 1px 보다 좁으면 그릴 열이 없어 실패한다(2026-10-06 사용자 확정). */
export const MIN_CYLINDER_PROJECTED_WIDTH_PX = 1;

/**
 * cylinder 서피스의 보이는 폭(points 가로 범위 = 투영 폭)을 그 면의 가장 좁은 색상 사진 px 로 바꿔 최소값을 검사한다.
 * 왕복 계산의 부동소수 오차로 1px 이 1px 아래로 떨어지지 않도록 허용오차만큼 여유를 둔다.
 */
const collectCylinderWidthIssues = (
  faceKey: string,
  face: FaceLike,
  colors: readonly ColorLike[],
  issues: DefinitionIssue[],
): void => {
  const photoWidths = colors.flatMap((color) => {
    const photo = color.faces[faceKey];
    return photo === undefined ? [] : [photo.photoSize.widthPx];
  });
  if (photoWidths.length === 0) return;
  const narrowestPhotoPx = Math.min(...photoWidths);

  face.surfaces.forEach((surface, index) => {
    if (surface.type !== 'cylinder') return;
    const [topLeft, topRight] = surface.points;
    const widthPx = ((topRight.x - topLeft.x) / 100) * narrowestPhotoPx;
    if (widthPx < MIN_CYLINDER_PROJECTED_WIDTH_PX + GEOMETRY_TOLERANCE) {
      issues.push({
        path: ['faces', faceKey, 'surfaces', index, 'points'],
        message: '원통 투영 폭이 면 사진에서 1px 보다 좁습니다.',
      });
    }
  });
};

const collectColorIssues = (
  colors: readonly ColorLike[],
  faceKeys: readonly string[],
  issues: DefinitionIssue[],
): void => {
  const seen = new Set<string>();

  colors.forEach((color, index) => {
    if (seen.has(color.colorOptionId)) {
      issues.push({ path: ['colors', index, 'colorOptionId'], message: '색상 옵션이 중복됩니다.' });
    }
    seen.add(color.colorOptionId);

    const colorFaceKeys = definedKeys(color.faces);
    const missing = faceKeys.filter((key) => !colorFaceKeys.includes(key));
    const extra = colorFaceKeys.filter((key) => !faceKeys.includes(key));

    if (missing.length > 0 || extra.length > 0) {
      issues.push({
        path: ['colors', index, 'faces'],
        message: `색상의 면 키 집합이 faces 와 다릅니다. (누락: ${missing.join(', ') || '없음'}, 초과: ${extra.join(', ') || '없음'})`,
      });
    }
  });
};

/** 어느 면이든 cylinder 서피스가 하나라도 있는가. api 의 원통 쓰기 게이트(ADR-0007 D11)가 쓴다. */
export const hasCylinderSurface = (definition: Pick<DefinitionLike, 'faces'>): boolean =>
  Object.values(definition.faces).some(
    (face) => face?.surfaces.some((surface) => surface.type === 'cylinder') ?? false,
  );

/** 어느 면이든 mesh 서피스가 하나라도 있는가. 곡면형 상품의 쓰기 게이트(ADR-0007 D11)가 쓴다. */
export const hasMeshSurface = (definition: Pick<DefinitionLike, 'faces'>): boolean =>
  Object.values(definition.faces).some(
    (face) => face?.surfaces.some((surface) => surface.type === 'mesh') ?? false,
  );

/**
 * 정의 전체의 관계 위반을 전부 모아 돌려준다. 빈 배열이면 유효하다.
 * zod 의 superRefine 이 각 항목을 issue 로 옮기므로 path 는 정의 루트 기준이다.
 */
export const collectDefinitionIssues = (definition: DefinitionLike): DefinitionIssue[] => {
  const issues: DefinitionIssue[] = [];
  const components = collectComponentIssues(definition.components, issues);
  const faceKeys = definedKeys(definition.faces);
  const surfaceIdsSeen = new Set<string>();
  const placedComponentIds = new Set<string>();

  for (const faceKey of faceKeys) {
    const face = definition.faces[faceKey];
    if (face !== undefined) {
      collectFaceIssues(faceKey, face, components, surfaceIdsSeen, placedComponentIds, issues);
      collectFaceCompositeIssues(faceKey, face, definition.colors, issues);
      collectCylinderWidthIssues(faceKey, face, definition.colors, issues);
    }
  }

  collectColorIssues(definition.colors, faceKeys, issues);

  definition.components.forEach((component, index) => {
    if (!placedComponentIds.has(component.id)) {
      issues.push({
        path: ['components', index],
        message: '어느 면에도 배치되지 않은 컴포넌트입니다.',
      });
    }
  });

  return issues;
};
